import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { formatNumber, formatPercent, formatPrice } from '../../lib/formatters';
import { SlidersHorizontal } from 'lucide-react';
import type { Assumptions } from '../../types';

function fmtShort(key: keyof Assumptions, value: number): string {
  switch (key) {
    case 's1Leads': return formatNumber(value);
    case 'kitReturnRate':
    case 'consultRate':
    case 'totalDeductionRate':
    case 's2CloseRate':
      return formatPercent(value, 0);
    case 'fixedCosts':
    case 's2RepCost':
      return formatPrice(value);
    case 's1CostPerUnit': return '$' + value.toFixed(0);
    case 'featureScaling': return value.toFixed(1);
    default: return String(value);
  }
}

function shortLabel(key: keyof Assumptions): string {
  switch (key) {
    case 's1Leads': return 'S1 Leads';
    case 'totalDeductionRate': return 'Deductions';
    case 's1CostPerUnit': return 'S1 Cost';
    case 's2CloseRate': return 'Close Rate';
    case 's2RepCost': return 'Rep Cost';
    case 'fixedCosts': return 'Fixed Costs';
    default: return key;
  }
}

const STRIP_KEYS: (keyof Assumptions)[] = [
  's1Leads', 'totalDeductionRate', 's1CostPerUnit', 's2CloseRate', 's2RepCost', 'fixedCosts',
];

export function AssumptionsStrip() {
  const assumptions = useSimulatorStore(s => s.assumptions);
  const toggleDrawer = useSimulatorStore(s => s.toggleAssumptionsDrawer);

  return (
    <div
      className="border-t cursor-pointer transition-all hover:opacity-90 accent-strip"
      style={{ borderColor: 'var(--color-border)' }}
      onClick={toggleDrawer}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleDrawer(); }}
    >
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 py-2 flex items-center gap-3 md:gap-5">
        <SlidersHorizontal size={13} className="text-chart-teal shrink-0" />

        <div className="flex items-center gap-4 overflow-x-auto flex-1 min-w-0">
          {STRIP_KEYS.map(key => (
            <div key={key} className="flex items-center gap-1.5 shrink-0">
              <span className="text-detail text-muted whitespace-nowrap">{shortLabel(key)}</span>
              <span className="text-detail font-mono font-semibold text-primary whitespace-nowrap">
                {fmtShort(key, assumptions[key] as number)}
              </span>
            </div>
          ))}
        </div>

        <span className="text-micro text-muted shrink-0 hidden lg:inline">Click to edit</span>
      </div>
    </div>
  );
}
